import { inject, Injectable } from '@angular/core';
import { IPerformanceLimitsConfig } from "@shared/interfaces/performance-limits.interface";
import { AppConstants } from "@core/constants";
import { HelperService } from "@shared/services/helper.service";
import {
  POWER_DROPDOWN_Y_AXIS_CONFIG,
  SPEED_DROPDOWN_Y_AXIS_CONFIG
} from "@shared/components/visualization/data-point-list.constant";
import { String } from "typescript-string-operations";
import { PerformanceDevelopmentYRangeNameEnum } from "@shared/enums/performance-development-y-range-name.enum";
import { X_AXIS_TYPE } from "@shared/enums/x-axis-type.enum";
import { PERFORMANCE_DEVELOPMENT_ORDER_ENUM } from "@shared/enums/performance-development-order.enum";
import { ChartService } from "@shared/services/chart.service";

@Injectable()
export class VisualizationService {
  private helperService: HelperService = inject(HelperService);
  private chartService: ChartService = inject(ChartService);

  private customAthleteCategoryObject: any = {};
  private defaultPDChartSettings: any = {
    x_axis_type: X_AXIS_TYPE.DATETIME,
    tools: 'pan,box_zoom',
    y_axis_label: 'Left Y axis 1',
    x_range: {bounds: ['auto'], plot_range: [0, 200]},
    layouts: [
      {
        axis_position: 'right',
        linear_axis: {
          axis_label_text_font_size: '10pt',
          y_range_name: PerformanceDevelopmentYRangeNameEnum.RIGHT_Y_1,
          axis_label: 'Right Y axis 1'
        },
      },
      {
        axis_position: 'left',
        linear_axis: {
          axis_label_text_font_size: "10pt",
          y_range_name: PerformanceDevelopmentYRangeNameEnum.LEFT_Y_2,
          axis_label: 'Left Y axis 2'
        },
      },
    ],
  };
  private fillColorList: string[] = ['yellow', 'pink', 'orange', 'red', 'blue', 'black'];
  private Y_LEFT_1_LABEL: string = 'Left Y axis 1';
  private Y_LEFT_2_LABEL: string = 'Left Y axis 2';
  private Y_RIGHT_1_LABEL: string = 'Right Y axis 1';

  public performanceDevelopmentOrderList: string[] = [];

  public resetCustomAthleteCategoryObject(): void {
    this.customAthleteCategoryObject = {};
  }

  public generateCustomAthleteCategoryObject(customAthleteCategoryList: any[]): any {
    this.resetCustomAthleteCategoryObject();

    customAthleteCategoryList.forEach((customCategory: any) => {
      this.customAthleteCategoryObject[customCategory.name] = {
        id: customCategory.value,
        name: customCategory.name,
        male_lower: 1,
        male_upper: 1,
        male_stdDev: 1,
        female_lower: 1,
        female_upper: 1,
        female_stdDev: 1,
      };
    });

    return this.customAthleteCategoryObject;
  }

  public getDefaultPerformanceLimitsTabConfig(metricName: string, customAthleteCategoryList: any[]): IPerformanceLimitsConfig {
    return {
      custom_tab_object: this.generateCustomAthleteCategoryObject(customAthleteCategoryList),
      tabs: {
        pro: {
          name: 'Pro',
          male_upper: 1,
          male_lower: 1,
          male_stdDev: 1,
          female_upper: 1,
          female_lower: 1,
          female_stdDev: 1,
        },
        amateur: {
          name: 'Amateur',
          male_lower: 1,
          male_upper: 1,
          male_stdDev: 1,
          female_lower: 1,
          female_upper: 1,
          female_stdDev: 1,
        },
        recreational: {
          name: 'Recreational',
          male_lower: 1,
          male_upper: 1,
          male_stdDev: 1,
          female_lower: 1,
          female_upper: 1,
          female_stdDev: 1,
        },
      },
      metricName: metricName,
      description: AppConstants.VISUALIZATION_PAGE.PERFORMANCE_LIMITS_DESCRIPTION,
    }
  }

  public getMetricNameWithUnit(tempMetricName: string, primaryType: string | undefined, secondaryType: string | undefined): string {
    let metricName: string | undefined;

    if (tempMetricName.includes('{0}')) {
      metricName = String.Format(tempMetricName, primaryType);
    } else if (tempMetricName.includes('{1}')) {
      metricName = String.Format(tempMetricName, secondaryType);
    } else {
      metricName = tempMetricName;
    }

    return metricName;
  }

  public generateExtraToolByYaxis(yAxis: any, simulationType: number, primaryType: string | undefined, secondaryType: string | undefined, yAxisLabel: string, xAxisType: string): any[] {
    return yAxis.map((item: any) => {
      const isSpeedBasedSport: boolean = this.helperService.isSpeedBasedSport(simulationType);
      let metricName: string | undefined = '';
      let tooltipValue: string = 'Date';

      if (isSpeedBasedSport) {
        metricName = SPEED_DROPDOWN_Y_AXIS_CONFIG.optionList.find((setting: any) => setting.value === item.value)?.name;
      } else {
        const tempMetricName: string = POWER_DROPDOWN_Y_AXIS_CONFIG.optionList.find((setting: any) => setting.value === item.value)?.name || '';

        metricName = this.getMetricNameWithUnit(tempMetricName, primaryType, secondaryType);
      }

      return {
        tooltips: [
          [metricName, ` @value ${yAxisLabel}`],
          [tooltipValue, ' @date']
        ]
      }
    });
  }

  public generateExtraTools(yObjectConfig: any, simulationType: number, primaryType: string | undefined, secondaryType: string | undefined, xAxisType: string): any {
    const y1ExtraTools: any[] = this.generateExtraToolByYaxis(yObjectConfig.y1Value, simulationType, primaryType, secondaryType, this.Y_LEFT_1_LABEL, xAxisType);
    const y2ExtraTools: any[] = this.generateExtraToolByYaxis(yObjectConfig.y2Value, simulationType, primaryType, secondaryType, this.Y_LEFT_2_LABEL, xAxisType);
    const y3ExtraTools: any[] = this.generateExtraToolByYaxis(yObjectConfig.y3Value, simulationType, primaryType, secondaryType, this.Y_RIGHT_1_LABEL, xAxisType);

    return [...y1ExtraTools, ...y2ExtraTools, ...y3ExtraTools];
  }

  public getRandomIntegerMinMax(min: number, max: number): number {
    let rand: number = min + Math.random() * (max + 1 - min);

    return Math.floor(rand);
  }

  public getRandomColor(colorList: string[]): string {
    const min: number = 0;
    const max: number = colorList.length;
    const randomIndex: number = Math.floor(Math.random() * (max - min + 1)) + min;

    return colorList[randomIndex];
  }

  public generateMaxMinDate(currentDataSet: number[]): any {
    return {
      x: [1704841200000, 1710457200000, 1721426400000, 1732489200000],
      y: currentDataSet,
      date: ['10-01-2024', '15-03-2024', '20-07-2024', '25-11-2024'],
      value: currentDataSet
    }
  }

  public getRandomSourceData(xAxisType: string, axis: any): any {
    if (axis.hasOwnProperty('current_data_set') && axis.current_data_set.length) {
      if (xAxisType === X_AXIS_TYPE.DATETIME) {
        return this.generateMaxMinDate(axis.current_data_set);
      }
    } else {
      return {
        x: [1704841200000, 1710457200000, 1721426400000, 1732489200000],
        y: [0, 0, 0, 0],
        date: ['10-01-2024', '15-03-2024', '20-07-2024', '25-11-2024'],
        value: [0, 0, 0, 0]
      }
    }
  }

  public generateLineByYAxis(yAxis: any, yRangeName: string, xAxisType: string): any[] {
    return yAxis.map((axis: any) => ({
      source: this.getRandomSourceData(xAxisType, axis),
      line_color: axis.color ? axis.color : '#000000de',
      line_width: 1,
      y_range_name: yRangeName,
    }))
  }

  public generateLines(yObjectConfig: any, xAxisType: string): any[] {
    const y1: any[] = this.generateLineByYAxis(yObjectConfig.y1Value, PerformanceDevelopmentYRangeNameEnum.LEFT_Y_1, xAxisType);
    const y2: any[] = this.generateLineByYAxis(yObjectConfig.y2Value, PerformanceDevelopmentYRangeNameEnum.LEFT_Y_2, xAxisType);
    const y3: any[] = this.generateLineByYAxis(yObjectConfig.y3Value, PerformanceDevelopmentYRangeNameEnum.RIGHT_Y_1, xAxisType);

    return [...y1, ...y2, ...y3];
  }

  public generateCircleByYRange(yAxis: any[], yRangeName: string): any[] {
    return yAxis.map(() => ({
      fill_color: 'white',
      size: 8,
      y_range_name: yRangeName,
    }))
  }

  public generateCircleList(yObjectConfig: any): any[] {
    const y1: any[] = this.generateCircleByYRange(yObjectConfig.y1Value, PerformanceDevelopmentYRangeNameEnum.LEFT_Y_1);
    const y2: any[] = this.generateCircleByYRange(yObjectConfig.y2Value, PerformanceDevelopmentYRangeNameEnum.LEFT_Y_2);
    const y3: any[] = this.generateCircleByYRange(yObjectConfig.y3Value, PerformanceDevelopmentYRangeNameEnum.RIGHT_Y_1);

    return [...y1, ...y2, ...y3];
  }

  public generateLayoutLegendList(yObjectConfig: any, simType: number, primaryType: string | undefined, secondaryType: string | undefined): any {
    const isSpeedBasedSport: boolean = this.helperService.isSpeedBasedSport(simType);
    const yAxisList: any[] = [];
    yObjectConfig.y1Value.forEach((yAxis: any) => {
      yAxis.yAxisPosition = 'Left Y axis 1';
      yAxisList.push(yAxis);
    });
    yObjectConfig.y2Value.forEach((yAxis: any) => {
      yAxis.yAxisPosition = 'Left Y axis 2';
      yAxisList.push(yAxis);
    });
    yObjectConfig.y3Value.forEach((yAxis: any) => {
      yAxis.yAxisPosition = 'Right Y axis 1';
      yAxisList.push(yAxis);
    });
    const layoutList: any[] = [];

    yAxisList.forEach((yAxis: any) => {
      if (yAxis.hasOwnProperty('value')) {
        if (isSpeedBasedSport) {
          const metricName: string | undefined = SPEED_DROPDOWN_Y_AXIS_CONFIG.optionList.find((setting: any) => setting.value === yAxis.value)?.name;
          if (metricName) {
            layoutList.push([metricName + ' ' + yAxis.yAxisPosition]);
          }
        } else {
          const tempMetricName: string = POWER_DROPDOWN_Y_AXIS_CONFIG.optionList.find((setting: any) => setting.value === yAxis.value)?.name + ' ' + yAxis.yAxisPosition || '';

          layoutList.push([this.getMetricNameWithUnit(tempMetricName, primaryType, secondaryType)]);
        }
      } else {
        layoutList.push(['N|A']);
      }
    });

    return {
      legends: layoutList,
    }
  }

  public generateChartConfig(y1Value: any,
                             y2Value: any,
                             y3Value: any,
                             simulationType: number,
                             primaryType: string | undefined,
                             secondaryType: string | undefined,
                             xAxisType: string,
                             ): any {
    const yAxisObjectConfig: any = {y1Value, y2Value, y3Value};
    const legendObject: any = this.generateLayoutLegendList(yAxisObjectConfig, simulationType, primaryType, secondaryType);
    const defaultPDSettings: any = this.helperService.makeCopy(this.defaultPDChartSettings);
    const config: any = {
      ...defaultPDSettings,
      extra_tools: this.generateExtraTools(yAxisObjectConfig, simulationType, primaryType, secondaryType, xAxisType),
      lines: this.generateLines(yAxisObjectConfig, X_AXIS_TYPE.DATETIME),
      circles: this.generateCircleList(yAxisObjectConfig),
      y_range: this.setYRangeByMetricValue(y1Value),
      extra_y_ranges: {
        leftY2: this.setYRangeByMetricValue(y2Value),
        rightY1: this.setYRangeByMetricValue(y3Value),
      },

    };
    config.layouts.push(legendObject);

    return config;
  }

  public setYRangeByMetricValue(yValue: any[]): any {
    const currentValue: number[] = [];
    let minMaxYAxis: any;

    yValue.forEach((item: any) => {
      if (item.hasOwnProperty('current_data_set')) {
        currentValue.push(...item.current_data_set)
      } else {
        currentValue.push(...[0, 100]);
      }
    });

    currentValue.sort((a: number, b: number) => a - b);


    minMaxYAxis = this.chartService.calculateOptimalBounds(currentValue);

    return {bounds: ['auto'], plot_range: [minMaxYAxis.min, minMaxYAxis.max]};
  }

  public setPerformanceDevelopmentOrder(value: string): void {
    this.performanceDevelopmentOrderList.push(value);
  }

  public getPerformanceDevelopmentOrder(): string[] {
    return this.performanceDevelopmentOrderList;
  }

  public resetPerformanceDevelopmentOrder(): string[] {
    return this.performanceDevelopmentOrderList = [];
  }

  public deletePerformanceDevelopmentOrderItem(name: any): void {
    const candidateToBeRemoved: number = this.performanceDevelopmentOrderList.indexOf(name);

    if (candidateToBeRemoved >= 0) {
      this.performanceDevelopmentOrderList.splice(candidateToBeRemoved, 1);
    }
  }

  public getLastPerformanceDevelopmentOrderItem(): string {
    return this.performanceDevelopmentOrderList[this.performanceDevelopmentOrderList.length-1];
  }

  public getDefaultPerformanceDevelopmentOrderList(): string[] {
    return [
      PERFORMANCE_DEVELOPMENT_ORDER_ENUM.FIRST,
      PERFORMANCE_DEVELOPMENT_ORDER_ENUM.SECOND,
      PERFORMANCE_DEVELOPMENT_ORDER_ENUM.THIRD,
    ];
  }

  public isExistsGraphItemInList(name: string): boolean {
    const pdCurrentOrder: string[] = this.getPerformanceDevelopmentOrder();

    return pdCurrentOrder.indexOf(name) >= 0;
  }
}
